import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { comingSoonService } from '../../../services/comingSoonService';

const ComingSoonManagement = () => {
  const [settings, setSettings] = useState({
    is_enabled: false,
    title: 'Coming Soon',
    description: 'We are working hard to bring you something amazing. Stay tuned!',
    bypass_password: '',
    background_image: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await comingSoonService?.getSettings();
      if (data) {
        setSettings({
          is_enabled: data?.is_enabled || false,
          title: data?.title || 'Coming Soon',
          description: data?.description || 'We are working hard to bring you something amazing. Stay tuned!',
          bypass_password: data?.bypass_password || '',
          background_image: data?.background_image || ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await comingSoonService?.updateSettings(settings);
      setSuccess('Settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleToggleStatus = async (enabled) => {
    try {
      setSaving(true);
      const updatedSettings = { ...settings, is_enabled: enabled };
      await comingSoonService?.updateSettings(updatedSettings);
      setSettings(updatedSettings);
      setSuccess(`Coming Soon mode ${enabled ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error toggling status:', error);
      setError('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground mt-4">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Status Control */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Coming Soon Status</h3>
            <p className="text-sm text-muted-foreground">
              Control whether the coming soon page blocks site access for non-admin users
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
              settings?.is_enabled 
                ? 'bg-warning/10 text-warning border border-warning/20' :'bg-success/10 text-success border border-success/20'
            }`}>
              <Icon name={settings?.is_enabled ? 'Lock' : 'Unlock'} size={16} />
              <span className="font-medium">
                {settings?.is_enabled ? 'Site Locked' : 'Site Open'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <Button
            variant={settings?.is_enabled ? 'outline' : 'default'}
            onClick={() => handleToggleStatus(false)}
            disabled={saving || !settings?.is_enabled}
            className={!settings?.is_enabled ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <Icon name="Unlock" size={16} className="mr-2" />
            Disable Coming Soon
          </Button>
          
          <Button
            variant={settings?.is_enabled ? 'default' : 'outline'}
            onClick={() => handleToggleStatus(true)}
            disabled={saving || settings?.is_enabled}
            className={settings?.is_enabled ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
          >
            <Icon name="Lock" size={16} className="mr-2" />
            Enable Coming Soon
          </Button>
        </div>

        {settings?.is_enabled && (
          <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" className="text-warning" size={16} />
              <p className="text-sm text-warning">
                <strong>Site is currently locked.</strong> Only admin users can access the site. 
                Regular users will see the coming soon page.
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Content Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Page Content</h3>
            <p className="text-sm text-muted-foreground">
              Customize the content displayed on the coming soon page
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Icon name="Eye" size={16} className="mr-2" />
            {previewMode ? 'Edit Mode' : 'Preview'}
          </Button>
        </div>

        {previewMode ? (
          <div className="border border-border rounded-lg p-8 bg-gradient-to-br from-muted/50 to-muted/20 text-center">
            <div className="max-w-md mx-auto">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {settings?.title}
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                {settings?.description}
              </p>
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-2">Password Bypass</h3>
                <div className="flex items-center space-x-2">
                  <Input
                    type="password"
                    placeholder="Enter bypass password"
                    className="flex-1"
                    disabled
                  />
                  <Button disabled>Enter</Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Page Title
              </label>
              <Input
                type="text"
                value={settings?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                placeholder="Coming Soon"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={settings?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Describe what visitors can expect..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Bypass Password
              </label>
              <Input
                type="text"
                value={settings?.bypass_password}
                onChange={(e) => handleInputChange('bypass_password', e?.target?.value)}
                placeholder="Enter bypass password for non-admin access"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Users can enter this password to bypass the coming soon page
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Background Image URL
              </label>
              <Input
                type="url"
                value={settings?.background_image}
                onChange={(e) => handleInputChange('background_image', e?.target?.value)}
                placeholder="https://example.com/image.jpg (optional)"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional background image for the coming soon page
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Alerts */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" className="text-destructive" size={16} />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-100 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" className="text-green-600" size={16} />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}
      {/* Save Button */}
      {!previewMode && (
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={loadSettings}
            disabled={saving}
          >
            Reset Changes
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default ComingSoonManagement;